/**
 * LeaveRequest.js
 *
 * FEATURE 9 — Multi-level Approval for Leave (this version)
 *   NEW: ApprovalChainConfig component (Admin/HR only, sits right below
 *   the existing CarryforwardConfig section). Per leave type, two simple
 *   checkboxes — "Require Reporting Manager approval" and "Require HR
 *   approval" — configure an optional 2-step chain (Manager always
 *   before HR). Both unchecked = single-step legacy behavior, unchanged
 *   from before this feature existed. Saves immediately on toggle, no
 *   separate Save button needed.
 *
 *   NEW: step-progress indicators. When a leave type has a chain
 *   configured, pending requests show "Step X of Y — awaiting Manager/HR"
 *   instead of a plain "Pending" badge, in both "My Leave Requests" and
 *   "Team Leave Requests" tables.
 *
 *   NEW: "History" button next to any multi-step request, opening a
 *   small modal with the full step-by-step audit trail (who approved/
 *   rejected at which step, when, with what remarks) via
 *   GET /leave/:id/approval-history.
 *
 *   Approve/Reject/Needs-Info buttons in the Team table continue to work
 *   exactly as before — the backend's GET /leave/all already filters out
 *   requests that have moved past a manager's step, so a manager simply
 *   won't see those rows at all once HR's turn comes up. No new frontend
 *   gating logic was needed for that.
 *
 * FEATURE 2 — Enhanced ESS Leave Balance Widget (earlier, unchanged)
 *   /leave/balance now returns year-scoped data with opening balance.
 *   Balance cards show Opening / Earned / Used / Remaining, FY label.
 *
 * FEATURE 1 — Leave Carryforward Config (earlier, unchanged)
 *   CarryforwardConfig component, Year-End Reset, All Employee Balances
 *   modal — all identical to before.
 *
 * All other existing behaviour is 100% unchanged.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Button, Table, Badge, Modal,
  Form, Spinner, Alert, InputGroup,
} from 'react-bootstrap';
import {
  BookOpen, Plus, Edit2, Trash2, Check, X,
  Clock, CheckCircle, XCircle, MessageSquare,
  ChevronLeft, ChevronRight, Settings, RefreshCw,
  Users, TrendingUp, ArrowRight, GitBranch, History,
} from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';

const STATUS_META = {
    pending:    { bg: 'warning',   icon: Clock,         label: 'Pending'           },
    approved:   { bg: 'success',   icon: CheckCircle,   label: 'Approved'          },
    rejected:   { bg: 'danger',    icon: XCircle,       label: 'Rejected'          },
    needs_info: { bg: 'info',      icon: MessageSquare, label: 'More Info Needed'  },
};
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const fmtC   = (n) => n ? `\u20B9${parseFloat(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '\u20B90';
const calcDays = (s, e) => (!s || !e) ? 0 : Math.max(1, Math.round((new Date(e) - new Date(s)) / 86400000) + 1);
// BUG FIX: req.start_date / req.end_date are pure calendar dates (e.g.
// "2026-06-26") with no time or timezone meaning at all — "26th June"
// should display as the 26th everywhere, regardless of browser timezone.
// new Date("2026-06-26") parses as UTC midnight per spec, and depending
// on exactly how the value round-trips through Sequelize/JSON before
// reaching here, re-displaying it via toLocaleDateString() in a positive
// UTC-offset timezone (e.g. IST) can shift it back by one calendar day —
// the same class of bug fixed earlier in ShiftRoster.js. Fixed by parsing
// the literal Y-M-D digits directly and never constructing a Date object
// that could be reinterpreted across a timezone boundary.
const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const fmtDateOnly = (dateStr) => {
    if (!dateStr) return '—';
    const ymd = String(dateStr).slice(0, 10); // "2026-06-26" even if a fuller ISO string arrives
    const [y, m, d] = ymd.split('-').map(Number);
    if (!y || !m || !d) return '—';
    return `${d} ${MONTH_SHORT[m - 1]}`;
};
const getCurrentLeaveYear = () => {
    const now = new Date();
    return now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
};
const stepLabel = (type) => type === 'manager' ? 'Manager' : type === 'hr' ? 'HR' : type;

const ConfirmModal = ({
    show, onHide, onConfirm, loading, title, message, confirmLabel,
    variant = 'danger', showRemarks, remarks, onRemarksChange, children,
}) => (
    <Modal show={show} onHide={onHide} centered size="sm">
        <Modal.Header closeButton className="border-0">
            <Modal.Title className="fw-bold fs-6">{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-3">
            <p className="text-muted mb-0 text-center" style={{ fontSize: 14 }}>{message}</p>
            {children}
            {showRemarks && (
                <Form.Group className="mt-3">
                    <Form.Label className="small fw-bold">Remarks {variant === 'danger' ? '(required)' : '(optional)'}</Form.Label>
                    <Form.Control as="textarea" rows={2}
                        placeholder="Add a note for the employee…"
                        value={remarks || ''} onChange={e => onRemarksChange(e.target.value)} />
                </Form.Group>
            )}
        </Modal.Body>
        <Modal.Footer className="border-0 justify-content-center gap-2">
            <Button variant="light" onClick={onHide} disabled={loading}>No, Cancel</Button>
            <Button variant={variant} onClick={onConfirm} disabled={loading}>
                {loading && <Spinner size="sm" className="me-1" />}{confirmLabel}
            </Button>
        </Modal.Footer>
    </Modal>
);

// FEATURE 9: APPROVAL HISTORY MODAL
const ApprovalHistoryModal = ({ requestId, onHide }) => {
    const [history, setHistory] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        axiosInstance.get(`/leave/${requestId}/approval-history`)
            .then(r => setHistory(r.data.data || []))
            .catch(() => toast.error('Failed to load approval history'))
            .finally(() => setLoading(false));
    }, [requestId]);
    const actionMeta = {
        approved:   { color: '#059669', label: 'Approved' },
        rejected:   { color: '#dc2626', label: 'Rejected' },
        needs_info: { color: '#0891b2', label: 'Requested more info' },
    };
    return (
        <Modal show onHide={onHide} centered>
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold fs-6">
                    <History size={16} className="me-2 text-primary" />Approval History
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
                ) : !history?.length ? (
                    <Alert variant="light" className="small mb-0 border">No approval actions recorded yet.</Alert>
                ) : (
                    <div className="d-flex flex-column gap-2">
                        {history.map((h, i) => {
                            const meta = actionMeta[h.action] || { color: '#64748b', label: h.action };
                            return (
                                <div key={i} className="p-2 rounded-3" style={{ background: '#f8fafc', borderLeft: `3px solid ${meta.color}` }}>
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div className="small fw-bold">
                                            Step {h.step_order} — {h.approver_name}
                                        </div>
                                        <Badge style={{ background: meta.color, fontSize: 10 }}>{meta.label}</Badge>
                                    </div>
                                    <div className="text-muted" style={{ fontSize: 11 }}>
                                        {new Date(h.acted_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                    {h.remarks && (
                                        <div className="small mt-1" style={{ fontStyle: 'italic' }}>"{h.remarks}"</div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="border-0">
                <Button variant="light" onClick={onHide}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
};

// FEATURE 9: APPROVAL CHAIN CONFIG (Admin/HR only)
const ApprovalChainConfig = () => {
    const [types,   setTypes]   = useState([]);
    const [chains,  setChains]  = useState({});
    const [loading, setLoading] = useState(true);
    const [savingId, setSavingId] = useState(null);
    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const typesRes = await axiosInstance.get('/leave/types');
            const allTypes = typesRes.data.data || [];
            setTypes(allTypes);
            const chainResults = await Promise.all(
                allTypes.map(t =>
                    axiosInstance.get(`/leave/approval-chain/${t.id}`)
                        .then(r => ({ id: t.id, data: r.data.data }))
                        .catch(() => ({ id: t.id, data: { require_manager: false, require_hr: false } }))
                )
            );
            const chainMap = {};
            chainResults.forEach(c => { chainMap[c.id] = c.data; });
            setChains(chainMap);
        } catch {
            toast.error('Failed to load approval chain configuration');
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { loadAll(); }, [loadAll]);
    const toggleStep = async (typeId, field, value) => {
        const current = chains[typeId] || { require_manager: false, require_hr: false };
        const updated  = { ...current, [field]: value };
        setChains(p => ({ ...p, [typeId]: updated }));
        setSavingId(typeId);
        try {
            await axiosInstance.put(`/leave/approval-chain/${typeId}`, {
                require_manager: updated.require_manager,
                require_hr:      updated.require_hr,
            });
            toast.success('Approval chain updated');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to update approval chain');
            setChains(p => ({ ...p, [typeId]: current }));
        } finally { setSavingId(null); }
    };
    const chainLabel = (c) => {
        if (!c?.require_manager && !c?.require_hr) return { text: 'Single-step (current behaviour)', color: '#94a3b8' };
        if (c.require_manager && c.require_hr)     return { text: 'Manager → HR', color: '#4f46e5' };
        if (c.require_manager)                      return { text: 'Manager only', color: '#0891b2' };
        return { text: 'HR only', color: '#7c3aed' };
    };
    return (
        <div className="mt-4">
            <div className="d-flex align-items-center gap-2 mb-3">
                <GitBranch size={16} className="text-primary" />
                <h6 className="fw-bold mb-0">Multi-Level Approval</h6>
                <Badge bg="info" style={{ fontSize: 10 }}>Admin / HR</Badge>
            </div>
            {loading ? (
                <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
            ) : (
                <Card className="border-0 shadow-sm">
                    <div className="p-2 small text-muted border-bottom" style={{ background: '#f8fafc' }}>
                        Require sign-off from the employee's Reporting Manager and/or HR before a leave request
                        of this type is approved. Leave both unchecked to keep the current single-step approval.
                    </div>
                    <Table hover responsive className="mb-0 small align-middle">
                        <thead className="bg-light">
                            <tr className="text-muted text-uppercase" style={{ fontSize: 11 }}>
                                <th className="ps-4">Leave Type</th>
                                <th className="text-center">Require Manager</th>
                                <th className="text-center">Require HR</th>
                                <th className="pe-4">Chain</th>
                            </tr>
                        </thead>
                        <tbody>
                            {types.map(t => {
                                const c = chains[t.id] || { require_manager: false, require_hr: false };
                                const label = chainLabel(c);
                                return (
                                    <tr key={t.id}>
                                        <td className="ps-4 fw-semibold">{t.name}</td>
                                        <td className="text-center">
                                            <Form.Check
                                                type="checkbox"
                                                checked={!!c.require_manager}
                                                disabled={savingId === t.id}
                                                onChange={e => toggleStep(t.id, 'require_manager', e.target.checked)}
                                                className="d-inline-block"
                                            />
                                        </td>
                                        <td className="text-center">
                                            <Form.Check
                                                type="checkbox"
                                                checked={!!c.require_hr}
                                                disabled={savingId === t.id}
                                                onChange={e => toggleStep(t.id, 'require_hr', e.target.checked)}
                                                className="d-inline-block"
                                            />
                                        </td>
                                        <td className="pe-4">
                                            <span className="fw-semibold" style={{ fontSize: 11, color: label.color }}>
                                                {savingId === t.id ? <Spinner size="sm" /> : label.text}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </Card>
            )}
        </div>
    );
};
// FEATURE 1: CARRYFORWARD CONFIG SECTION (Admin/HR only) — unchanged
const CarryforwardConfig = ({ onResetDone }) => {
    const [config,       setConfig]       = useState([]);
    const [loading,      setLoading]      = useState(true);
    const [editingId,    setEditingId]    = useState(null);
    const [editValues,   setEditValues]   = useState({});
    const [saving,       setSaving]       = useState(false);
    const [resetModal,   setResetModal]   = useState(false);
    const [resetLoading, setResetLoading] = useState(false);
    const [resetResult,  setResetResult]  = useState(null);
    const [balanceModal, setBalanceModal] = useState(false);
    const [balances,     setBalances]     = useState(null);
    const [balLoading,   setBalLoading]   = useState(false);
    const [balYear,      setBalYear]      = useState(getCurrentLeaveYear());
    const CURR_YEAR = new Date().getFullYear();
    const loadConfig = useCallback(() => {
        setLoading(true);
        axiosInstance.get('/leave/year-config')
            .then(r => setConfig(r.data.data || []))
            .catch(() => toast.error('Failed to load carryforward config'))
            .finally(() => setLoading(false));
    }, []);
    useEffect(() => { loadConfig(); }, [loadConfig]);
    const startEdit = (row) => {
        setEditingId(row.id);
        setEditValues({ carry_forward_days: row.carry_forward_days, lapse_unused: !!row.lapse_unused });
    };
    const saveEdit = async (typeId) => {
        setSaving(true);
        try {
            await axiosInstance.put(`/leave/year-config/${typeId}`, editValues);
            toast.success('Carryforward policy saved');
            setEditingId(null);
            loadConfig();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Save failed');
        } finally { setSaving(false); }
    };
    const handleReset = async () => {
        setResetLoading(true);
        try {
            const res = await axiosInstance.post('/leave/year-end-reset');
            setResetResult(res.data.data);
            setResetModal(false);
            toast.success(`Year-end reset complete — ${res.data.data.employees_processed} employees processed`);
            onResetDone?.();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Year-end reset failed');
        } finally { setResetLoading(false); }
    };
    const loadBalances = () => {
        setBalLoading(true);
        axiosInstance.get(`/leave/year-balance/all?year=${balYear}`)
            .then(r => setBalances(r.data.data))
            .catch(() => toast.error('Failed to load balances'))
            .finally(() => setBalLoading(false));
    };
    const closingYear = getCurrentLeaveYear() - 1;
    return (
        <div className="mt-4">
            <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2">
                    <Settings size={16} className="text-primary" />
                    <h6 className="fw-bold mb-0">Leave Carryforward Settings</h6>
                    <Badge bg="info" style={{ fontSize: 10 }}>Admin / HR</Badge>
                </div>
                <div className="d-flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline-primary"
                        onClick={() => { setBalanceModal(true); loadBalances(); }}
                        style={{ borderRadius: 8 }}>
                        <Users size={13} className="me-1" />All Employee Balances
                    </Button>
                    <Button size="sm" variant="outline-warning"
                        onClick={() => setResetModal(true)}
                        style={{ borderRadius: 8 }}>
                        <RefreshCw size={13} className="me-1" />Year-End Reset
                    </Button>
                </div>
            </div>
            {resetResult && (
                <Alert variant="success" className="small mb-3" onClose={() => setResetResult(null)} dismissible>
                    <strong>Reset complete</strong> — {resetResult.closed_year} closed.&nbsp;
                    {resetResult.employees_processed} employees,&nbsp;
                    {resetResult.total_days_carried} days carried forward,&nbsp;
                    {resetResult.total_days_lapsed} days lapsed.
                </Alert>
            )}
            {loading ? (
                <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
            ) : (
                <Card className="border-0 shadow-sm">
                    <div className="p-2 small text-muted border-bottom" style={{ background: '#f8fafc' }}>
                        Configure how many unused leave days each type carries forward to the next year (Indian FY: April 1 → March 31).
                    </div>
                    <Table hover responsive className="mb-0 small align-middle">
                        <thead className="bg-light">
                            <tr className="text-muted text-uppercase" style={{ fontSize: 11 }}>
                                <th className="ps-4">Leave Type</th>
                                <th className="text-center">Annual Days</th>
                                <th className="text-center">Max Carry-forward Days</th>
                                <th className="text-center">Lapse Unused?</th>
                                <th className="pe-4 text-end">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {config.map(row => (
                                <tr key={row.id}>
                                    <td className="ps-4 fw-semibold">{row.name}</td>
                                    <td className="text-center">{row.max_days}</td>
                                    <td className="text-center">
                                        {editingId === row.id ? (
                                            <Form.Control
                                                type="number" min="0" max={row.max_days}
                                                value={editValues.carry_forward_days}
                                                onChange={e => setEditValues(p => ({ ...p, carry_forward_days: parseInt(e.target.value) || 0 }))}
                                                style={{ width: 80, display: 'inline-block', textAlign: 'center' }}
                                                size="sm"
                                            />
                                        ) : (
                                            <span style={{ fontWeight: row.carry_forward_days > 0 ? 700 : 400, color: row.carry_forward_days > 0 ? '#059669' : '#94a3b8' }}>
                                                {row.carry_forward_days > 0 ? `${row.carry_forward_days} days` : 'None'}
                                            </span>
                                        )}
                                    </td>
                                    <td className="text-center">
                                        {editingId === row.id ? (
                                            <Form.Check
                                                type="switch"
                                                checked={editValues.lapse_unused}
                                                onChange={e => setEditValues(p => ({ ...p, lapse_unused: e.target.checked }))}
                                                label=""
                                            />
                                        ) : (
                                            <Badge bg={row.lapse_unused ? 'danger' : 'light'} text={row.lapse_unused ? 'white' : 'dark'} style={{ fontSize: 10 }}>
                                                {row.lapse_unused ? 'Lapses' : 'No lapse'}
                                            </Badge>
                                        )}
                                    </td>
                                    <td className="pe-4 text-end">
                                        {editingId === row.id ? (
                                            <div className="d-flex justify-content-end gap-1">
                                                <Button size="sm" variant="success" disabled={saving}
                                                    onClick={() => saveEdit(row.id)} style={{ borderRadius: 6 }}>
                                                    {saving ? <Spinner size="sm" /> : <Check size={13} />}
                                                </Button>
                                                <Button size="sm" variant="light"
                                                    onClick={() => setEditingId(null)} style={{ borderRadius: 6 }}>
                                                    <X size={13} />
                                                </Button>
                                            </div>
                                        ) : (
                                            <Button size="sm" variant="outline-secondary"
                                                onClick={() => startEdit(row)} style={{ borderRadius: 6 }}>
                                                <Edit2 size={13} />
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card>
            )}
            <ConfirmModal
                show={resetModal}
                onHide={() => setResetModal(false)}
                onConfirm={handleReset}
                loading={resetLoading}
                title="Year-End Leave Reset"
                confirmLabel="Run Reset"
                variant="warning"
                message="">
                <div className="text-center mt-2">
                    <div className="fw-bold" style={{ fontSize: 15 }}>
                        Close FY {closingYear}-{String(closingYear + 1).slice(-2)}
                    </div>
                    <p className="text-muted small mt-2 mb-0">
                        This will calculate carryforward for all active employees
                        based on the policies above and set opening balances for
                        FY {closingYear + 1}-{String(closingYear + 2).slice(-2)}.
                    </p>
                    <Alert variant="warning" className="small mt-3 mb-0 text-start">
                        This action processes all active employees. It is safe to run
                        multiple times — it uses upsert logic and will not duplicate data.
                    </Alert>
                </div>
            </ConfirmModal>
            <Modal show={balanceModal} onHide={() => setBalanceModal(false)} centered size="xl">
                <Modal.Header closeButton className="border-0">
                    <div>
                        <Modal.Title className="fw-bold fs-6">
                            <Users size={16} className="me-2 text-primary" />
                            All Employee Leave Balances
                        </Modal.Title>
                        <div className="text-muted small mt-1">
                            Showing balances for FY {balYear}-{String(balYear + 1).slice(-2)}
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex gap-2 align-items-center mb-3 flex-wrap">
                        <Form.Select size="sm" value={balYear}
                            onChange={e => setBalYear(parseInt(e.target.value))}
                            style={{ width: 160 }}>
                            {[CURR_YEAR, CURR_YEAR - 1, CURR_YEAR - 2].map(y => (
                                <option key={y} value={y}>FY {y}-{String(y + 1).slice(-2)}</option>
                            ))}
                        </Form.Select>
                        <Button size="sm" variant="outline-primary" onClick={loadBalances}>
                            <RefreshCw size={13} className="me-1" />Load
                        </Button>
                    </div>
                    {balLoading ? (
                        <div className="text-center py-4"><Spinner animation="border" /></div>
                    ) : !balances ? (
                        <Alert variant="info" className="small">Click Load to fetch balances.</Alert>
                    ) : balances.rows.length === 0 ? (
                        <Alert variant="warning" className="small">No active employee data found for this year.</Alert>
                    ) : (
                        <div style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                            <Table hover responsive className="mb-0 small align-middle" style={{ fontSize: 12 }}>
                                <thead className="bg-light" style={{ position: 'sticky', top: 0 }}>
                                    <tr className="text-muted text-uppercase" style={{ fontSize: 10 }}>
                                        <th className="ps-3">Employee</th>
                                        <th>Department</th>
                                        <th>Leave Type</th>
                                        <th className="text-center">Opening</th>
                                        <th className="text-center">Earned</th>
                                        <th className="text-center">Total</th>
                                        <th className="text-center">Used</th>
                                        <th className="text-center">Remaining</th>
                                        <th className="text-center">Carried</th>
                                        <th className="pe-3 text-center">Lapsed</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {balances.rows.map((r, i) => (
                                        <tr key={i}>
                                            <td className="ps-3">
                                                <div className="fw-semibold">{r.employee_name}</div>
                                                <div className="text-muted" style={{ fontSize: 10 }}>{r.employee_code}</div>
                                            </td>
                                            <td>{r.department || '—'}</td>
                                            <td>{r.leave_type}</td>
                                            <td className="text-center">
                                                {r.opening_balance > 0 ? (
                                                    <Badge bg="success" style={{ fontSize: 10 }}>+{r.opening_balance}</Badge>
                                                ) : '—'}
                                            </td>
                                            <td className="text-center">{r.earned_days}</td>
                                            <td className="text-center fw-bold">{parseFloat(r.total_available || 0)}</td>
                                            <td className="text-center text-danger">{parseFloat(r.days_used || 0)}</td>
                                            <td className="text-center">
                                                <span style={{ fontWeight: 700, color: r.remaining > 0 ? '#059669' : '#94a3b8' }}>
                                                    {parseFloat(r.remaining || 0)}
                                                </span>
                                            </td>
                                            <td className="text-center">
                                                {r.carried_to_next > 0 ? (
                                                    <Badge bg="primary" style={{ fontSize: 10 }}>{r.carried_to_next}</Badge>
                                                ) : '—'}
                                            </td>
                                            <td className="pe-3 text-center">
                                                {r.lapsed > 0 ? (
                                                    <Badge bg="danger" style={{ fontSize: 10 }}>{r.lapsed}</Badge>
                                                ) : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="light" onClick={() => setBalanceModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};
// MAIN COMPONENT
const LeaveRequest = () => {
    const { user } = useAuth();
    const canSelfSubmit  = true;
    const isEmployee     = user?.role === 'employee';
    const isApproverRole = ['super-admin','admin','hr-manager','account-manager'].includes(user?.role);
    const [hasDirectReports, setHasDirectReports] = useState(false);
    useEffect(() => {
        if (user?.role === 'employee') {
            axiosInstance.get('/employees/my-reports')
                .then(r => setHasDirectReports((r.data.data || []).length > 0))
                .catch(() => {});
        }
    }, [user?.role]);
    const canApprove = isApproverRole || hasDirectReports;
    const now = new Date();
    const [month,       setMonth]       = useState(now.getMonth() + 1);
    const [year,        setYear]        = useState(now.getFullYear());
    const [myRequests,  setMyRequests]  = useState([]);
    const [allRequests, setAllRequests] = useState([]);
    const [leaveTypes,  setLeaveTypes]  = useState([]);
    const [balance,     setBalance]     = useState([]);
    const [loading,     setLoading]     = useState(true);
    const [showForm,    setShowForm]    = useState(false);
    const [editingId,   setEditingId]   = useState(null);
    const [form,        setForm]        = useState({ leave_type_id:'', start_date:'', end_date:'', reason:'' });
    const [errors,      setErrors]      = useState({});
    const [saving,      setSaving]      = useState(false);
    const [cancelModal, setCancelModal] = useState({ show:false, id:null, loading:false });
    const [reviewModal, setReviewModal] = useState({ show:false, id:null, status:'', loading:false, remarks:'' });
    const [historyModalId, setHistoryModalId] = useState(null); // FEATURE 9
    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const calls = [
                axiosInstance.get('/leave/types'),
                axiosInstance.get('/leave/my-requests'),
                axiosInstance.get('/leave/balance'),
            ];
            if (canApprove) calls.push(axiosInstance.get('/leave/all'));
            const results = await Promise.allSettled(calls);
            if (results[0].status === 'fulfilled') setLeaveTypes(results[0].value.data.data || []);
            if (results[1].status === 'fulfilled') setMyRequests(results[1].value.data.data || []);
            if (results[2].status === 'fulfilled') setBalance(results[2].value.data.data || []);
            if (results[3]?.status === 'fulfilled') setAllRequests(results[3].value.data.data || []);
        } catch { toast.error('Failed to load leave data'); }
        finally { setLoading(false); }
    }, [canApprove]);
    useEffect(() => { fetchAll(); }, [fetchAll]);
    const validateForm = () => {
        const e = {};
        if (!form.leave_type_id) e.leave_type_id = 'Please select a leave type';
        if (!form.start_date)    e.start_date    = 'Start date is required';
        if (!form.end_date)      e.end_date      = 'End date is required';
        if (form.start_date) {
            const minD = new Date(); minD.setDate(minD.getDate() - 7); minD.setHours(0,0,0,0);
            if (new Date(form.start_date) < minD) e.start_date = 'Cannot apply leave more than 1 week in the past';
        }
        if (form.start_date && form.end_date && form.end_date < form.start_date)
            e.end_date = 'End date must be after start date';
        return e;
    };
    const openEdit = (req) => {
        setEditingId(req.id);
        setForm({ leave_type_id: req.leave_type_id, start_date: req.start_date?.slice(0,10), end_date: req.end_date?.slice(0,10), reason: req.reason || '' });
        setErrors({});
        setShowForm(true);
    };
    const openAdd = () => {
        setEditingId(null);
        setForm({ leave_type_id:'', start_date:'', end_date:'', reason:'' });
        setErrors({});
        setShowForm(true);
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        const errs = validateForm();
        if (Object.keys(errs).length) { setErrors(errs); return; }
        setSaving(true);
        try {
            if (editingId) {
                await axiosInstance.put(`/leave/${editingId}`, form);
                toast.success('Leave request updated');
            } else {
                await axiosInstance.post('/leave/request', form);
                toast.success('Leave request submitted! Your manager and HR have been notified.');
            }
            setShowForm(false);
            fetchAll();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed to submit'); }
        finally { setSaving(false); }
    };
    const handleCancel = async () => {
        setCancelModal(p => ({ ...p, loading: true }));
        try {
            await axiosInstance.delete(`/leave/${cancelModal.id}`);
            toast.success('Leave request cancelled');
            setCancelModal({ show:false, id:null, loading:false });
            fetchAll();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Cancel failed');
            setCancelModal(p => ({ ...p, loading: false }));
        }
    };
    const handleReview = async () => {
        if (reviewModal.status === 'needs_info' && !reviewModal.remarks?.trim())
            return toast.error('Please provide remarks when requesting more information');
        setReviewModal(p => ({ ...p, loading: true }));
        try {
            await axiosInstance.patch(`/leave/review/${reviewModal.id}`, {
                status: reviewModal.status, remarks: reviewModal.remarks
            });
            const action = reviewModal.status === 'approved' ? 'approved' :
                           reviewModal.status === 'rejected' ? 'rejected' : 'updated';
            toast.success(`Leave request ${action}. Employee has been notified.`);
            setReviewModal({ show:false, id:null, status:'', loading:false, remarks:'' });
            fetchAll();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Review failed');
            setReviewModal(p => ({ ...p, loading: false }));
        }
    };
    const navMonth = (dir) => {
        let m = month + dir, y = year;
        if (m > 12) { m = 1;  y++; }
        if (m < 1)  { m = 12; y--; }
        setMonth(m); setYear(y);
    };
    const filteredMine = myRequests.filter(r => {
        const d = new Date(r.start_date);
        return d.getMonth() + 1 === month && d.getFullYear() === year;
    });
    const filteredAll = allRequests.filter(r => {
        const d = new Date(r.start_date);
        return d.getMonth() + 1 === month && d.getFullYear() === year;
    });
    const pendingApprovals = allRequests.filter(r => r.status === 'pending');
    // Get leave year label from first balance item (all same)
    const leaveYearLabel = balance[0]?.leave_year_label || `${getCurrentLeaveYear()}-${String(getCurrentLeaveYear() + 1).slice(-2)}`;
    // FEATURE 9: status cell renderer — shows step progress for multi-step
    // requests instead of a plain badge, falls back to plain badge otherwise.
    const renderStatusCell = (req) => {
        const meta = STATUS_META[req.status] || STATUS_META.pending;
        const Icon = meta.icon;
        if (req.status === 'pending' && req.approval_chain_length > 0) {
            // BUG FIX: defensively clamp the displayed step number to the
            // chain length. The backend now also clamps current_step
            // whenever a chain is reconfigured (see PUT
            // /leave/approval-chain/:typeId), but this guard ensures the
            // UI can never show a nonsensical value like "Step 2 of 1"
            // even if some edge case left stale data unclamped.
            const displayStep = Math.min(req.current_step, req.approval_chain_length);
            return (
                <div>
                    <Badge bg="warning" text="dark" className="d-inline-flex align-items-center gap-1 px-2 py-1">
                        <Clock size={11} />Step {displayStep} of {req.approval_chain_length}
                    </Badge>
                    <div className="text-muted mt-1" style={{ fontSize: 10 }}>
                        awaiting {stepLabel(req.current_step_type || req.current_step_label)}
                    </div>
                </div>
            );
        }
        return (
            <Badge bg={meta.bg} className="d-inline-flex align-items-center gap-1 px-2 py-1">
                <Icon size={11} />{meta.label}
            </Badge>
        );
    };
    return (
        <div className="p-2 p-md-4">
            {/* Header */}
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
                <div>
                    <h4 className="fw-bold mb-1"><BookOpen size={22} className="me-2 text-primary" />Leave Management</h4>
                    <p className="text-muted small mb-0">
                        {canApprove
                            ? `${pendingApprovals.length} pending approval${pendingApprovals.length !== 1 ? 's' : ''} · You can also apply for your own leave below`
                            : 'Apply for leave and track your requests'}
                    </p>
                </div>
                {canSelfSubmit && (
                    <Button variant="primary" onClick={openAdd}>
                        <Plus size={18} className="me-1" />Apply for Leave
                    </Button>
                )}
            </div>
            {/* Month navigator */}
            <div className="d-flex align-items-center justify-content-center gap-3 mb-4">
                <Button variant="outline-secondary" size="sm" onClick={() => navMonth(-1)}><ChevronLeft size={16} /></Button>
                <h6 className="mb-0 fw-bold" style={{ minWidth: 130, textAlign: 'center' }}>{MONTHS[month-1]} {year}</h6>
                <Button variant="outline-secondary" size="sm" onClick={() => navMonth(1)}
                    disabled={month === now.getMonth()+1 && year === now.getFullYear()}>
                    <ChevronRight size={16} />
                </Button>
            </div>
            {/* Leave Balance Cards */}
            {balance.length > 0 && (
                <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className="small fw-bold text-muted" style={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Leave Balance — FY {leaveYearLabel}
                        </div>
                    </div>
                    <Row className="g-3">
                        {balance.map(b => {
                            const opening   = parseFloat(b.opening_balance  || 0);
                            const earned    = parseFloat(b.max_days         || 0);
                            const used      = parseFloat(b.days_used        || 0);
                            const total     = parseFloat(b.total_available  || earned);
                            const remaining = parseFloat(b.remaining        || Math.max(0, total - used));
                            const pct       = total > 0 ? Math.round((used / total) * 100) : 0;
                            const isLow     = remaining <= 2 && remaining > 0;
                            const isGone    = remaining === 0;
                            const color     = isGone ? '#dc2626' : isLow ? '#d97706' : '#4f46e5';
                            return (
                                <Col key={b.id} xs={6} md={3}>
                                    <Card className="border-0 shadow-sm p-3 h-100" style={{ borderTop: `3px solid ${color}` }}>
                                        <div className="small text-muted fw-bold mb-2" style={{ fontSize: 12 }}>{b.name}</div>
                                        <div className="d-flex align-items-baseline gap-1 mb-2">
                                            <span style={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1 }}>
                                                {remaining}
                                            </span>
                                            <span className="text-muted small">/ {total} days</span>
                                        </div>
                                        <div className="progress mb-2" style={{ height: 5 }}>
                                            <div className="progress-bar" style={{
                                                width: `${pct}%`,
                                                background: pct >= 80 ? '#dc2626' : pct >= 50 ? '#d97706' : '#4f46e5',
                                            }} />
                                        </div>
                                        <div style={{ fontSize: 11, color: '#64748b' }}>
                                            <div className="d-flex justify-content-between">
                                                <span>Earned</span>
                                                <span className="fw-semibold">{earned}d</span>
                                            </div>
                                            {opening > 0 && (
                                                <div className="d-flex justify-content-between">
                                                    <span>+ Carried fwd</span>
                                                    <span className="fw-semibold text-success">+{opening}d</span>
                                                </div>
                                            )}
                                            <div className="d-flex justify-content-between">
                                                <span>Used</span>
                                                <span className="fw-semibold text-danger">{used}d</span>
                                            </div>
                                            <div className="d-flex justify-content-between border-top mt-1 pt-1">
                                                <span className="fw-bold">Remaining</span>
                                                <span className="fw-bold" style={{ color }}>{remaining}d</span>
                                            </div>
                                        </div>
                                        {isGone && (
                                            <Badge bg="danger" className="mt-2 w-100 text-center" style={{ fontSize: 10 }}>
                                                Exhausted
                                            </Badge>
                                        )}
                                        {isLow && !isGone && (
                                            <Badge bg="warning" text="dark" className="mt-2 w-100 text-center" style={{ fontSize: 10 }}>
                                                Low balance
                                            </Badge>
                                        )}
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>
                </div>
            )}
            {/* Pending approvals alert */}
            {canApprove && pendingApprovals.length > 0 && (
                <Alert variant="warning" className="d-flex align-items-center gap-2 small mb-3">
                    <Clock size={16} className="flex-shrink-0" />
                    <span><strong>{pendingApprovals.length} leave request{pendingApprovals.length !== 1 ? 's' : ''}</strong> pending your review.</span>
                </Alert>
            )}
            {loading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
            ) : (
                <>
                    {/* My requests */}
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Header className="bg-white fw-bold border-0 pt-3">
                            My Leave Requests — {MONTHS[month-1]} {year}
                        </Card.Header>
                        <Table hover responsive className="mb-0 small align-middle">
                            <thead className="bg-light">
                                <tr className="text-muted text-uppercase" style={{ fontSize: 11 }}>
                                    <th className="ps-4">Type</th>
                                    <th>From</th>
                                    <th>To</th>
                                    <th>Days</th>
                                    <th>Status</th>
                                    <th>Reviewed By</th>
                                    <th>Remarks</th>
                                    <th className="text-end pe-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredMine.length > 0 ? filteredMine.map(req => {
                                    const isPending = req.status === 'pending' || req.status === 'needs_info';
                                    return (
                                        <tr key={req.id} style={{ background: isPending ? '#fffbeb' : undefined }}>
                                            <td className="ps-4 fw-medium">{req.type_name}</td>
                                            <td>{fmtDateOnly(req.start_date)}</td>
                                            <td>{fmtDateOnly(req.end_date)}</td>
                                            <td><strong>{calcDays(req.start_date, req.end_date)}</strong></td>
                                            <td>{renderStatusCell(req)}</td>
                                            <td>{req.reviewer_name || <span className="text-muted">—</span>}</td>
                                            <td style={{ maxWidth: 160 }}>
                                                <div style={{ fontSize: 11, color: '#64748b', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                                    {req.remarks || '—'}
                                                </div>
                                            </td>
                                            <td className="text-end pe-4">
                                                <div className="d-flex justify-content-end gap-1">
                                                    {req.approval_chain_length > 0 && (
                                                        <Button size="sm" variant="outline-secondary" className="py-0 px-2"
                                                            title="View approval history"
                                                            onClick={() => setHistoryModalId(req.id)}>
                                                            <History size={13} />
                                                        </Button>
                                                    )}
                                                    {isPending && (
                                                        <>
                                                            <Button size="sm" variant="outline-secondary" className="py-0 px-2"
                                                                title="Edit request" onClick={() => openEdit(req)}>
                                                                <Edit2 size={13} />
                                                            </Button>
                                                            <Button size="sm" variant="outline-danger" className="py-0 px-2"
                                                                title="Cancel request"
                                                                onClick={() => setCancelModal({ show:true, id:req.id, loading:false })}>
                                                                <Trash2 size={13} />
                                                            </Button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                }) : (
                                    <tr><td colSpan="8" className="text-center py-4 text-muted">
                                        No leave requests for {MONTHS[month-1]} {year}
                                        <Button variant="link" size="sm" onClick={openAdd}>Apply now</Button>
                                    </td></tr>
                                )}
                            </tbody>
                        </Table>
                    </Card>
                    {/* Team approval table */}
                    {canApprove && (
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Header className="bg-white fw-bold border-0 pt-3 d-flex justify-content-between align-items-center">
                                <span>Team Leave Requests — {MONTHS[month-1]} {year}</span>
                                <Badge bg="warning" text="dark">{pendingApprovals.length} pending</Badge>
                            </Card.Header>
                            <Table hover responsive className="mb-0 small align-middle">
                                <thead className="bg-light">
                                    <tr className="text-muted text-uppercase" style={{ fontSize: 11 }}>
                                        <th className="ps-4">Employee</th>
                                        <th>Type</th>
                                        <th>From</th>
                                        <th>To</th>
                                        <th>Days</th>
                                        <th>Reason</th>
                                        <th>Status</th>
                                        <th className="text-end pe-4">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredAll.length > 0 ? filteredAll.map(req => (
                                            <tr key={req.id} style={{ background: req.status === 'pending' ? '#fffbeb' : undefined }}>
                                                <td className="ps-4">
                                                    <div className="fw-medium">{req.first_name} {req.last_name}</div>
                                                    {req.employee_code && <div className="text-muted" style={{ fontSize: 10 }}>{req.employee_code}</div>}
                                                </td>
                                                <td>{req.type_name}</td>
                                                <td>{fmtDateOnly(req.start_date)}</td>
                                                <td>{fmtDateOnly(req.end_date)}</td>
                                                <td><strong>{calcDays(req.start_date, req.end_date)}</strong></td>
                                                <td style={{ maxWidth: 150 }}>
                                                    <div style={{ fontSize: 11, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                                        {req.reason || '—'}
                                                    </div>
                                                </td>
                                                <td>{renderStatusCell(req)}</td>
                                                <td className="text-end pe-4">
                                                    <div className="d-flex justify-content-end gap-1">
                                                        {req.approval_chain_length > 0 && (
                                                            <Button size="sm" variant="outline-secondary" className="py-0 px-2"
                                                                title="View approval history"
                                                                onClick={() => setHistoryModalId(req.id)}>
                                                                <History size={13} />
                                                            </Button>
                                                        )}
                                                        {req.status === 'pending' ? (
                                                            <>
                                                                <Button size="sm" variant="success" className="py-0 px-2"
                                                                    onClick={() => setReviewModal({ show:true, id:req.id, status:'approved', loading:false, remarks:'' })}>
                                                                    <Check size={13} /> Approve
                                                                </Button>
                                                                <Button size="sm" variant="outline-danger" className="py-0 px-2"
                                                                    onClick={() => setReviewModal({ show:true, id:req.id, status:'rejected', loading:false, remarks:'' })}>
                                                                    <X size={13} /> Reject
                                                                </Button>
                                                                <Button size="sm" variant="outline-info" className="py-0 px-2"
                                                                    title="Request more info"
                                                                    onClick={() => setReviewModal({ show:true, id:req.id, status:'needs_info', loading:false, remarks:'' })}>
                                                                    <MessageSquare size={13} />
                                                                </Button>
                                                            </>
                                                        ) : (
                                                            <span className="text-muted" style={{ fontSize: 11 }}>
                                                                {req.reviewer_name ? `by ${req.reviewer_name}` : '—'}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )) : (
                                        <tr><td colSpan="8" className="text-center py-4 text-muted">
                                            No team leave requests for {MONTHS[month-1]} {year}
                                        </td></tr>
                                    )}
                                </tbody>
                            </Table>
                        </Card>
                    )}
                    {/* Admin/HR config sections */}
                    {isApproverRole && (
                        <>
                            <ApprovalChainConfig />
                            <CarryforwardConfig onResetDone={fetchAll} />
                        </>
                    )}
                </>
            )}
            {/* Apply / Edit Modal */}
            <Modal show={showForm} onHide={() => setShowForm(false)} centered>
                <Modal.Header closeButton className="border-0">
                    <Modal.Title className="fw-bold">{editingId ? 'Edit Leave Request' : 'Apply for Leave'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {editingId && (
                        <Alert variant={myRequests.find(r => r.id === editingId)?.status === 'needs_info' ? 'warning' : 'info'} className="small py-2 mb-3">
                            {myRequests.find(r => r.id === editingId)?.status === 'needs_info'
                                ? '💬 Your manager has requested more information. Update your reason below and resubmit.'
                                : "You can edit this request as long as it hasn't been reviewed yet."}
                        </Alert>
                    )}
                    <Form noValidate onSubmit={handleSubmit}>
                        <Row className="g-3">
                            <Col xs={12}>
                                <Form.Label className="small fw-bold">Leave Type *</Form.Label>
                                <Form.Select value={form.leave_type_id}
                                    onChange={e => { setForm(p => ({...p, leave_type_id: e.target.value})); setErrors(p => ({...p, leave_type_id:''})); }}
                                    isInvalid={!!errors.leave_type_id}>
                                    <option value="">Select leave type…</option>
                                    {leaveTypes.map(t => (
                                        <option key={t.id} value={t.id}>{t.name} ({t.max_days} days/year)</option>
                                    ))}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">{errors.leave_type_id}</Form.Control.Feedback>
                                {form.leave_type_id && (() => {
                                    const b = balance.find(x => String(x.id) === String(form.leave_type_id));
                                    if (!b) return null;
                                    const rem = parseFloat(b.remaining || 0);
                                    return (
                                        <div className="mt-1 small" style={{ color: rem <= 2 ? '#dc2626' : '#059669' }}>
                                            {rem} day{rem !== 1 ? 's' : ''} remaining this year
                                            {b.opening_balance > 0 && ` (includes ${b.opening_balance} carried forward)`}
                                        </div>
                                    );
                                })()}
                            </Col>
                            <Col xs={12} sm={6}>
                                <Form.Label className="small fw-bold">From Date *</Form.Label>
                                <Form.Control type="date" value={form.start_date}
                                    min={(() => { const d = new Date(); d.setDate(d.getDate()-7); return d.toISOString().slice(0,10); })()}
                                    max={(() => { const d = new Date(); d.setDate(d.getDate()+90); return d.toISOString().slice(0,10); })()}
                                    onChange={e => { setForm(p => ({...p, start_date: e.target.value})); setErrors(p => ({...p, start_date:''})); }}
                                    isInvalid={!!errors.start_date} />
                                <Form.Control.Feedback type="invalid">{errors.start_date}</Form.Control.Feedback>
                            </Col>
                            <Col xs={12} sm={6}>
                                <Form.Label className="small fw-bold">To Date *</Form.Label>
                                <Form.Control type="date" value={form.end_date} min={form.start_date}
                                    onChange={e => { setForm(p => ({...p, end_date: e.target.value})); setErrors(p => ({...p, end_date:''})); }}
                                    isInvalid={!!errors.end_date} />
                                <Form.Control.Feedback type="invalid">{errors.end_date}</Form.Control.Feedback>
                            </Col>
                            {form.start_date && form.end_date && form.end_date >= form.start_date && (
                                <Col xs={12}>
                                    <div className="p-2 bg-light rounded-3 small text-center">
                                        <strong>{calcDays(form.start_date, form.end_date)} day{calcDays(form.start_date, form.end_date) !== 1 ? 's' : ''}</strong> of leave
                                    </div>
                                </Col>
                            )}
                            <Col xs={12}>
                                <Form.Label className="small fw-bold">Reason</Form.Label>
                                <Form.Control as="textarea" rows={3} placeholder="Brief reason for leave…"
                                    value={form.reason} onChange={e => setForm(p => ({...p, reason: e.target.value}))} />
                            </Col>
                        </Row>
                        <div className="d-flex gap-2 mt-4">
                            <Button variant="light" type="button" className="flex-grow-1" onClick={() => setShowForm(false)}>Cancel</Button>
                            <Button variant="primary" type="submit" className="flex-grow-1 fw-bold" disabled={saving}>
                                {saving ? <Spinner size="sm" /> : editingId ? 'Update Request' : 'Submit Request'}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
            {/* Cancel Confirm */}
            <ConfirmModal
                show={cancelModal.show}
                onHide={() => setCancelModal({ show:false, id:null, loading:false })}
                onConfirm={handleCancel}
                loading={cancelModal.loading}
                title="Cancel Leave Request"
                message="Are you sure you want to cancel this leave request? This cannot be undone."
                confirmLabel="Yes, Cancel Request"
                variant="danger"
            />
            {/* Review Modal */}
            <ConfirmModal
                show={reviewModal.show}
                onHide={() => setReviewModal({ show:false, id:null, status:'', loading:false, remarks:'' })}
                onConfirm={handleReview}
                loading={reviewModal.loading}
                title={
                    reviewModal.status === 'approved' ? 'Approve Leave Request' :
                    reviewModal.status === 'rejected' ? 'Reject Leave Request'  :
                    'Request More Information'
                }
                message={
                    reviewModal.status === 'approved' ? 'Approve this leave request? The employee will be notified.' :
                    reviewModal.status === 'rejected' ? 'Reject this leave request? Please provide a reason below.' :
                    'Ask the employee for more details. They will be notified.'
                }
                confirmLabel={
                    reviewModal.status === 'approved' ? 'Yes, Approve' :
                    reviewModal.status === 'rejected' ? 'Yes, Reject'  :
                    'Send Request'
                }
                variant={
                    reviewModal.status === 'approved' ? 'success' :
                    reviewModal.status === 'rejected' ? 'danger'  :
                    'info'
                }
                showRemarks={true}
                remarks={reviewModal.remarks}
                onRemarksChange={v => setReviewModal(p => ({...p, remarks: v}))}
            />
            {/* FEATURE 9: Approval History Modal */}
            {historyModalId && (
                <ApprovalHistoryModal requestId={historyModalId} onHide={() => setHistoryModalId(null)} />
            )}
        </div>
    );
};
export default LeaveRequest;
